import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, CheckCircle, ChevronDown, ChevronLeft, ChevronRight, RefreshCw, Search, SlidersHorizontal } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import transactionApi from '../../../api/transactionApi';
import ledgerViewsApi, { ArWeeklyActionRecord, ArWeeklyMetricRecord, ArWeeklyRevenueContributionRecord, LedgerDatasetItem } from '../../../api/ledgerViewsApi';
import { AccountingLedgerSource, AccountingWorkspaceView, ARWorkspaceView, DocInvoice, LedgerDatasetFilterParams, LedgerViewMode, TransactionFilterParams, UploadedLedgerRecord } from '../../../api/types';
import { useUser } from '../../../context/UserContext';
import { useWorkspace } from '../../../context/WorkspaceContext';
import userMetadataApi from '../../../api/userMetadataApi';
import SearchableBusinessSelect from '../../../components/SearchableBusinessSelect';
import InvoiceConfirmationModal from '../../documents/components/InvoiceConfirmationModal';
import LedgerTable from '../cash/CashTable';
import LedgerFilters from '../cash/CashFilters';
import AccountingFilters from '../accounting/AccountingFilters';
import ARFilters from '../ar/ARFilters';
import APFilters from '../ap/APFilters';
import InventoryFilters from '../inventory/InventoryFilters';
import AccountingDatasetTable from '../accounting/AccountingDatasetTable';
import ARCustomersTable from '../ar/ARCustomersTable';
import ARLedgerTable from '../ar/ARLedgerTable';
import APVendorsTable from '../ap/APVendorsTable';
import APLedgerTable from '../ap/APLedgerTable';
import InventoryItemsTable from '../inventory/InventoryItemsTable';
import LedgerRecordDrawer from '../components/LedgerRecordDrawer';
import CashView from '../cash/CashView';
import AccountingView from '../accounting/AccountingView';
import ARView from '../ar/ARView';
import APView from '../ap/APView';
import InventoryView from '../inventory/InventoryView';
import useCashQueries from '../cash/useCashQueries';
import useAccountingQueries from '../accounting/useAccountingQueries';
import useARQueries from '../ar/useARQueries';
import useInventoryQueries from '../inventory/useInventoryQueries';
import useLedgerDatasetQueries from '../shared/useLedgerDatasetQueries';
import StandardAccountSelector from '../components/StandardAccountSelector';
import TransactionDrawer from '../cash/TransactionDrawer';
import VendorDirectoryModal from '../ap/VendorDirectoryModal';
import VendorManagementModal from '../ap/VendorManagementModal';
import ArWeeklySummaryModal from '../ar/ArWeeklySummaryModal';
import WeeklySummaryModal from '../../signals/components/WeeklySummaryModal';
import ChatButton from '../../../components/ChatButton';
import { useIsMobile } from '../../../utils/hooks';
import LedgerChatPanel from '../components/LedgerChatPanel';
import { LedgerAgentOpenNormalizationEditorPayload } from '../../../api/ledgerAgentApi';
import { endOfWeekSunday, formatDateKey, startOfWeekMonday } from '../../../utils/tutorialDates';
import { isTutorialRuntimeActive } from '../../../components/tutorial/tutorialRuntime';

type AgentFilterChip = {
    label: string;
    value: string;
};

const LEDGER_VIEW_OPTIONS: Array<{ value: LedgerViewMode; label: string }> = [
    { value: 'cash', label: 'Cash View' },
    { value: 'accounting', label: 'Accounting View' },
    { value: 'ar', label: 'AP/AR View' },
    { value: 'inventory', label: 'Inventory View' },
];

const LEDGER_MOBILE_VIEW_LABELS: Record<LedgerViewMode, string> = {
    cash: 'Cash',
    accounting: 'Acct',
    ar: 'AP/AR',
    inventory: 'Inv',
};

const LEDGER_TUTORIAL_AR_CUSTOMERS: LedgerDatasetItem[] = [
    {
        _id: 'tutorial-ar-customer-1',
        customer_id: 'tutorial-customer-a',
        customer_name: 'Customer A',
        open_invoice_count: 1,
        total_invoice_count: 3,
        total_invoiced_amount: 2369.52,
        paid_amount_total: 0,
        open_ar: 2369.52,
        overdue_ar: 2369.52,
        partial_payment_frequency: 1,
        collection_risk_level: 'low',
        concentration_share: 0.302,
    },
    {
        _id: 'tutorial-ar-customer-2',
        customer_id: 'tutorial-customer-b',
        customer_name: 'Customer B',
        open_invoice_count: 2,
        total_invoice_count: 4,
        total_invoiced_amount: 772,
        paid_amount_total: 772,
        open_ar: 772,
        overdue_ar: 772,
        partial_payment_frequency: 0,
        collection_risk_level: 'low',
        concentration_share: 0.11,
    },
    {
        _id: 'tutorial-ar-customer-3',
        customer_id: 'tutorial-customer-c',
        customer_name: 'Customer C',
        open_invoice_count: 3,
        total_invoice_count: 5,
        total_invoiced_amount: 772,
        paid_amount_total: 772,
        open_ar: 552,
        overdue_ar: 552,
        partial_payment_frequency: 0,
        collection_risk_level: 'low',
        concentration_share: 0.07,
    },
];

const buildLedgerTutorialArMetric = (today: Date = new Date()): ArWeeklyMetricRecord => {
    const weekStart = startOfWeekMonday(today);
    const weekEnd = endOfWeekSunday(today);

    return {
        business_id: 'tutorial-business',
        week_start: formatDateKey(weekStart),
        week_end: formatDateKey(weekEnd),
        actual_revenue_linked_cash: 6420,
        expected_cash_due: 8950,
        missed_expected_cash_amount: 2530,
        open_ar: 18670,
        total_overdue_amount: 11784,
        new_overdue_amount: 1960,
        unapplied_cash_amount: 4280,
        partial_payment_amount: 3140,
    };
};

const buildLedgerTutorialArActions = (today: Date = new Date()): ArWeeklyActionRecord[] => {
    const weekStart = formatDateKey(startOfWeekMonday(today));

    return [
        { business_id: 'tutorial-business', week_start: weekStart, action_type: 'review_partial_payment', title: 'Partial payment detected', customer_name: 'Customer A', customer_id: 'tutorial-customer-a', invoice_number: '1004', amount: 2370, status: 'open' },
        { business_id: 'tutorial-business', week_start: weekStart, action_type: 'adjust_cash_expectation', title: 'Expected receipt missed', customer_name: 'Customer B', customer_id: 'tutorial-customer-b', invoice_number: '1007', amount: 1960, status: 'open' },
        { business_id: 'tutorial-business', week_start: weekStart, action_type: 'follow_up_invoice', title: 'Follow up on overdue invoice', customer_name: 'Customer C', customer_id: 'tutorial-customer-c', invoice_number: '0969', amount: 772, status: 'open' },
    ];
};

const buildLedgerTutorialArContributions = (today: Date = new Date()): ArWeeklyRevenueContributionRecord[] => {
    const weekStart = formatDateKey(startOfWeekMonday(today));

    return [
        { business_id: 'tutorial-business', contribution_id: 'tutorial-contribution-1', week_start: weekStart, customer_name: 'Customer A', invoice_number: '1004', allocated_amount: 2370, allocation_type: 'matched_receipt', included_in_weekly_revenue: true },
        { business_id: 'tutorial-business', contribution_id: 'tutorial-contribution-2', week_start: weekStart, customer_name: 'Customer B', invoice_number: '1007', allocated_amount: 1960, allocation_type: 'expected_missed', included_in_weekly_revenue: false, exclusion_reason: 'expected_receipt_missed' },
    ];
};

const areTransactionFiltersEqual = (
    left: TransactionFilterParams,
    right: TransactionFilterParams,
): boolean => JSON.stringify(left) === JSON.stringify(right);

const parseAgentDateFilter = (value: string): Pick<TransactionFilterParams, 'start_date' | 'end_date'> => {
    const trimmed = String(value || '').trim();
    const rangeMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})\s+to\s+(\d{4}-\d{2}-\d{2})$/i);
    if (rangeMatch) {
        return { start_date: rangeMatch[1], end_date: rangeMatch[2] };
    }
    const fromMatch = trimmed.match(/^from\s+(\d{4}-\d{2}-\d{2})$/i);
    if (fromMatch) {
        return { start_date: fromMatch[1], end_date: undefined };
    }
    const throughMatch = trimmed.match(/^through\s+(\d{4}-\d{2}-\d{2})$/i);
    if (throughMatch) {
        return { start_date: undefined, end_date: throughMatch[1] };
    }
    return {};
};

const parseAgentAmountFilter = (value: string): Pick<TransactionFilterParams, 'amount_gt' | 'amount_gte' | 'amount_lt' | 'amount_lte'> => {
    const next: Pick<TransactionFilterParams, 'amount_gt' | 'amount_gte' | 'amount_lt' | 'amount_lte'> = {};
    const matcher = /(>=|<=|>|<)\s*\$?\s*([\d,]+(?:\.\d+)?)/g;
    const text = String(value || '');
    let match: RegExpExecArray | null;
    while ((match = matcher.exec(text)) !== null) {
        const comparator = match[1];
        const parsed = Number(match[2].replace(/,/g, ''));
        if (!Number.isFinite(parsed)) continue;
        if (comparator === '>') next.amount_gt = parsed;
        if (comparator === '>=') next.amount_gte = parsed;
        if (comparator === '<') next.amount_lt = parsed;
        if (comparator === '<=') next.amount_lte = parsed;
    }
    return next;
};

const formatLocalDateInput = (value: Date | null): string | undefined => {
    if (!value) return undefined;
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const parseLocalDateInput = (value?: string | null): Date | null => {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
};

const getApArDefaultSortBy = (source: ARWorkspaceView): string => (
    source === 'ar_customers'
        ? 'overdue_ar'
        : source === 'ar_ledger'
            ? 'event_date'
            : source === 'ap_vendors'
                ? 'open_ap'
                : 'effective_payment_date'
);

const getApArDefaultSortOrder = (source: ARWorkspaceView): 'asc' | 'desc' => (
    source === 'ap_ledger' ? 'desc' : 'desc'
);

const isApWorkspaceSource = (source: ARWorkspaceView): boolean => (
    source === 'ap_bills' || source === 'ap_vendors' || source === 'ap_ledger'
);

const resetDatasetFiltersForWorkspace = (
    prev: Omit<LedgerDatasetFilterParams, 'source'>,
    viewMode: LedgerViewMode,
    accountingSource: AccountingWorkspaceView,
    arSource: ARWorkspaceView,
): Omit<LedgerDatasetFilterParams, 'source'> => ({
    ...prev,
    page: 1,
    status: undefined,
    record_type: viewMode === 'ar' && (arSource === 'ar_ledger' || arSource === 'ap_ledger') ? prev.record_type : undefined,
    transaction_type: undefined,
    account_name: undefined,
    split_account_name: undefined,
    balance_state: undefined,
    has_price_changes: undefined,
    inventory_currency: undefined,
    inventory_vendor_name: undefined,
    inventory_history_state: undefined,
    due_this_week: viewMode === 'ar' && (arSource === 'ar_ledger' || arSource === 'ap_ledger' || arSource === 'ap_bills') ? prev.due_this_week : undefined,
    overdue_only: viewMode === 'ar' && (arSource === 'ap_ledger' || arSource === 'ap_bills') ? prev.overdue_only : undefined,
    critical_only: viewMode === 'ar' && (arSource === 'ap_ledger' || arSource === 'ap_bills') ? prev.critical_only : undefined,
    unapplied_cash: viewMode === 'ar' && arSource === 'ar_ledger' ? prev.unapplied_cash : undefined,
    sort_by: viewMode === 'inventory'
        ? 'most_recent_seen_at'
        : viewMode === 'ar'
            ? getApArDefaultSortBy(arSource)
            : accountingSource === 'gl_code'
                ? prev.sort_by
                : 'transaction_date',
    sort_order: viewMode === 'ar' ? getApArDefaultSortOrder(arSource) : 'desc',
});

const REFRESH_SETTLE_POLL_ATTEMPTS = 30;
const REFRESH_SETTLE_POLL_DELAY_MS = 2000;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => {
    window.setTimeout(resolve, ms);
});

const findMatchingDatasetItem = (
    items: LedgerDatasetItem[],
    selected: LedgerDatasetItem | null,
): LedgerDatasetItem | null => {
    if (!selected) return null;
    const candidateKeys = [
        '_id',
        'ledger_row_id',
        'customer_id',
        'invoice_id',
        'bill_id',
        'transaction_id',
        'file_id',
        'catalog_id',
        'item_key',
        'source_id',
    ] as const;
    const selectedKeys = new Set(
        candidateKeys
            .map((key) => String((selected as Record<string, unknown>)[key] || '').trim())
            .filter((value) => value.length > 0),
    );
    if (selectedKeys.size === 0) return null;
    return items.find((item) => (
        candidateKeys.some((key) => {
            const value = String((item as Record<string, unknown>)[key] || '').trim();
            return value.length > 0 && selectedKeys.has(value);
        })
    )) || null;
};

const LedgerScreen: React.FC = () => {
    const isMobile = useIsMobile();
    const location = useLocation();
    const navigate = useNavigate();
    const isAdminEmbedded = location.pathname.startsWith('/admin/business/');
    const queryClient = useQueryClient();
    const { isAdmin } = useUser();
    const { workspaces, activeBusinessId, setActiveBusinessId } = useWorkspace();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialAccountId = searchParams.get('financial_account_id');
    const deepLinkView = searchParams.get('view');
    const deepLinkSource = searchParams.get('source');
    const deepLinkQboBillId = searchParams.get('qbo_bill_id');
    const deepLinkVendorName = searchParams.get('vendor_name');
    const deepLinkVendorExact = searchParams.get('vendor_exact') === 'true';
    const deepLinkSearch = searchParams.get('search');
    const deepLinkCatalogId = searchParams.get('catalog_id');
    const deepLinkDirection = searchParams.get('direction');
    const deepLinkOperatingFlag = searchParams.get('operating_flag');
    const openedInventoryCatalogDeepLinkRef = useRef<string | null>(null);

    const [ledgerViewMode, setLedgerViewMode] = useState<LedgerViewMode>('cash');
    const [accountingSource, setAccountingSource] = useState<AccountingWorkspaceView>('transaction_list');
    const [arSource, setArSource] = useState<ARWorkspaceView>('ar_customers');
    const [cashFilters, setCashFilters] = useState<TransactionFilterParams>({
        page: 1,
        page_size: 50,
        financial_account_id: initialAccountId || undefined,
        vendor_name: deepLinkVendorName || undefined,
        vendor_exact: deepLinkVendorName ? deepLinkVendorExact : undefined,
        search: deepLinkSearch || deepLinkCatalogId || undefined,
        direction: deepLinkDirection === 'inflow' || deepLinkDirection === 'outflow' ? deepLinkDirection : undefined,
        operating_flag: deepLinkOperatingFlag === 'true' ? true : deepLinkOperatingFlag === 'false' ? false : undefined,
    });
    const [datasetFilters, setDatasetFilters] = useState<Omit<LedgerDatasetFilterParams, 'source'>>({
        page: 1,
        page_size: 50,
        search: deepLinkSearch || deepLinkCatalogId || deepLinkVendorName || undefined,
        sort_by: ledgerViewMode === 'inventory' ? 'most_recent_seen_at' : 'transaction_date',
        sort_order: 'desc',
    });
    const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
    const [pendingNormalizationEditorRequest, setPendingNormalizationEditorRequest] = useState<{
        transactionId: string;
        token: number;
        matchType?: 'contains' | 'regex' | 'exact' | string;
        matchValue?: string;
        normalizedDescription?: string;
        vendorName?: string;
    } | null>(null);
    const [selectedDatasetItem, setSelectedDatasetItem] = useState<LedgerDatasetItem | null>(null);
    const [selectedDatasetSource, setSelectedDatasetSource] = useState<AccountingLedgerSource | 'inventory' | null>(null);
    const [hasUserSortedDataset, setHasUserSortedDataset] = useState(false);
    const [isVendorManagementOpen, setIsVendorManagementOpen] = useState(false);
    const [vendorDirectorySource, setVendorDirectorySource] = useState<'aliases' | 'qbo' | 'qbo_customers' | null>(null);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [isBatchUpdating, setIsBatchUpdating] = useState(false);
    const [reviewDocumentRecord, setReviewDocumentRecord] = useState<UploadedLedgerRecord | null>(null);
    const [reviewDocumentType, setReviewDocumentType] = useState<'invoice' | 'expenses' | 'bills' | null>(null);
    const [glCodeQboType, setGlCodeQboType] = useState('');
    const [glCodeClassification, setGlCodeClassification] = useState('');
    const [glCodeFinzCategory, setGlCodeFinzCategory] = useState('');
    const [isGlCodeFinzSelectorOpen, setIsGlCodeFinzSelectorOpen] = useState(false);
    const [showChat, setShowChat] = useState(false);
    const [extendChat, setExtendChat] = useState(false);
    const [showSetupGuidePanel, setShowSetupGuidePanel] = useState(false);
    const [agentAppliedFilters, setAgentAppliedFilters] = useState<AgentFilterChip[]>([]);
    const [isRefreshSettling, setIsRefreshSettling] = useState(false);
    const [isArWeeklySummaryOpen, setIsArWeeklySummaryOpen] = useState(false);
    const [isUnifiedWeeklySummaryOpen, setIsUnifiedWeeklySummaryOpen] = useState(false);
    const [isViewModeMenuOpen, setIsViewModeMenuOpen] = useState(false);
    const [isCashMobileFiltersOpen, setIsCashMobileFiltersOpen] = useState(false);
    const [isTutorialDemo, setIsTutorialDemo] = useState(() => isTutorialRuntimeActive('ledger'));
    const viewModeMenuRef = useRef<HTMLDivElement | null>(null);
    const mobileViewModeMenuRef = useRef<HTMLDivElement | null>(null);
    const cashMobileFiltersRef = useRef<HTMLDivElement | null>(null);
    const ledgerTutorialArMetric = useMemo(() => buildLedgerTutorialArMetric(), []);
    const ledgerTutorialArActions = useMemo(() => buildLedgerTutorialArActions(), []);
    const ledgerTutorialArContributions = useMemo(() => buildLedgerTutorialArContributions(), []);

    const userBusinessOptions = useMemo(() => (
        Array.from(
            new Map(
                workspaces.flatMap((workspace) =>
                    (workspace.businesses || []).map((business) => [
                        business.id,
                        {
                            id: business.id,
                            name: business.legal_name || business.dba_name || 'Unnamed Business',
                        },
                    ]),
                ),
            ).values(),
        )
    ), [workspaces]);
    const { data: allBusinessesData } = useQuery({
        queryKey: ['allBusinesses'],
        queryFn: userMetadataApi.getAllBusinesses,
        enabled: !!isAdmin,
    });
    const adminBusinessOptions = useMemo(() => (
        allBusinessesData?.businesses.map((business) => ({
            id: business.business_id || business.user_id,
            name: business.legal_name || business.dba_name || business.onboarding?.qbo_metadata?.company_name || business.user_email || 'Unknown',
            subtitle: business.user_email || undefined,
        })) || []
    ), [allBusinessesData]);

    const businessOptions = isAdmin ? adminBusinessOptions : userBusinessOptions;
    const selectedBusinessId = cashFilters.business_id || datasetFilters.business_id;
    const canFetchLedgerData = !!selectedBusinessId;
    useEffect(() => {
        if (activeBusinessId) {
            setCashFilters((prev) => ({ ...prev, business_id: activeBusinessId, qbo_account_id: undefined }));
            setDatasetFilters((prev) => ({ ...prev, business_id: activeBusinessId }));
        }
    }, [activeBusinessId]);

    useEffect(() => {
        const handleSetupGuideVisibility = (event: Event) => {
            setShowSetupGuidePanel(Boolean((event as CustomEvent<{ open?: boolean }>).detail?.open));
        };

        setShowSetupGuidePanel(document.body.classList.contains('finz-setup-guide-open'));
        window.addEventListener('finz:setup-guide:visibility', handleSetupGuideVisibility);
        return () => window.removeEventListener('finz:setup-guide:visibility', handleSetupGuideVisibility);
    }, []);

    useEffect(() => {
        if (!isViewModeMenuOpen) return undefined;
        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (viewModeMenuRef.current?.contains(target) || mobileViewModeMenuRef.current?.contains(target)) return;
            setIsViewModeMenuOpen(false);
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [isViewModeMenuOpen]);

    useEffect(() => {
        if (!isCashMobileFiltersOpen) return undefined;
        const handlePointerDown = (event: MouseEvent) => {
            if (cashMobileFiltersRef.current?.contains(event.target as Node)) return;
            setIsCashMobileFiltersOpen(false);
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [isCashMobileFiltersOpen]);

    useEffect(() => {
        if (!isMobile) {
            setIsCashMobileFiltersOpen(false);
        }
    }, [isMobile]);

    useEffect(() => {
        const handleTutorialStep = (event: Event) => {
            const detail = (event as CustomEvent).detail as { pageKey?: string } | undefined;
            if (detail?.pageKey === 'ledger') setIsTutorialDemo(true);
        };
        const handleTutorialActive = (event: Event) => {
            const detail = (event as CustomEvent<{ pageKey?: string; active?: boolean }>).detail;
            setIsTutorialDemo(detail?.active !== false && detail?.pageKey === 'ledger');
        };
        const openViewDropdown = () => {
            setIsTutorialDemo(true);
            setLedgerViewMode('cash');
            setIsArWeeklySummaryOpen(false);
            setIsUnifiedWeeklySummaryOpen(false);
            window.setTimeout(() => setIsViewModeMenuOpen(true), 0);
        };
        const showArCustomers = () => {
            setIsTutorialDemo(true);
            setIsViewModeMenuOpen(false);
            setLedgerViewMode('ar');
            setArSource('ar_customers');
            setIsArWeeklySummaryOpen(false);
            setIsUnifiedWeeklySummaryOpen(false);
        };
        const openArWeeklySummary = () => {
            setIsTutorialDemo(true);
            setIsViewModeMenuOpen(false);
            setLedgerViewMode('ar');
            setArSource('ar_customers');
            window.setTimeout(() => setIsArWeeklySummaryOpen(true), 0);
        };
        const handleTutorialClosed = (event: Event) => {
            const detail = (event as CustomEvent).detail as { pageKey?: string } | undefined;
            if (detail?.pageKey !== 'ledger') return;
            setIsTutorialDemo(false);
            setIsViewModeMenuOpen(false);
            setIsArWeeklySummaryOpen(false);
        };

        if (isTutorialRuntimeActive('ledger')) setIsTutorialDemo(true);
        window.addEventListener('finz:tutorial:active', handleTutorialActive);
        window.addEventListener('finz:tutorial:step', handleTutorialStep);
        window.addEventListener('finz:tutorial:ledger-open-view-dropdown', openViewDropdown);
        window.addEventListener('finz:tutorial:ledger-show-ar-customers', showArCustomers);
        window.addEventListener('finz:tutorial:ledger-open-ar-summary', openArWeeklySummary);
        window.addEventListener('finz:tutorial:closed', handleTutorialClosed);
        return () => {
            window.removeEventListener('finz:tutorial:active', handleTutorialActive);
            window.removeEventListener('finz:tutorial:step', handleTutorialStep);
            window.removeEventListener('finz:tutorial:ledger-open-view-dropdown', openViewDropdown);
            window.removeEventListener('finz:tutorial:ledger-show-ar-customers', showArCustomers);
            window.removeEventListener('finz:tutorial:ledger-open-ar-summary', openArWeeklySummary);
            window.removeEventListener('finz:tutorial:closed', handleTutorialClosed);
        };
    }, []);

    useEffect(() => {
        if (ledgerViewMode !== 'cash') {
            setSelectedTransactionId(null);
            setSelectedIds([]);
        }
        if (ledgerViewMode === 'cash') {
            setSelectedDatasetItem(null);
            setSelectedDatasetSource(null);
            setHasUserSortedDataset(false);
        }
    }, [ledgerViewMode]);

    useEffect(() => {
        if (deepLinkView === 'accounting') {
            setLedgerViewMode('accounting');
        }
        if (deepLinkView === 'ar') {
            setLedgerViewMode('ar');
        }
        if (deepLinkView === 'cash') {
            setLedgerViewMode('cash');
        }
        if (deepLinkView === 'inventory' || deepLinkSource === 'inventory') {
            setLedgerViewMode('inventory');
        }
        if (deepLinkSource === 'bills' || deepLinkSource === 'invoices' || deepLinkSource === 'transaction_list' || deepLinkSource === 'general_ledger' || deepLinkSource === 'ar') {
            if (deepLinkSource === 'ar') {
                setLedgerViewMode('ar');
            } else {
                setAccountingSource(deepLinkSource);
            }
        }
        if (deepLinkSource === 'ap_ledger' || deepLinkSource === 'ap_vendors' || deepLinkSource === 'ap_bills' || deepLinkSource === 'ar_ledger' || deepLinkSource === 'ar_customers') {
            setLedgerViewMode('ar');
            setArSource(deepLinkSource as ARWorkspaceView);
        }
    }, [deepLinkSource, deepLinkView]);

    useEffect(() => {
        setCashFilters((prev) => ({
            ...prev,
            vendor_name: deepLinkVendorName || undefined,
            vendor_exact: deepLinkVendorName ? deepLinkVendorExact : undefined,
            search: deepLinkSearch || deepLinkCatalogId || undefined,
            direction: deepLinkDirection === 'inflow' || deepLinkDirection === 'outflow' ? deepLinkDirection : undefined,
            operating_flag: deepLinkOperatingFlag === 'true' ? true : deepLinkOperatingFlag === 'false' ? false : undefined,
            page: 1,
        }));
        setDatasetFilters((prev) => ({
            ...prev,
            search: deepLinkSearch || deepLinkCatalogId || deepLinkVendorName || undefined,
            page: 1,
        }));
    }, [deepLinkVendorName, deepLinkVendorExact, deepLinkSearch, deepLinkCatalogId, deepLinkDirection, deepLinkOperatingFlag]);

    useEffect(() => {
        if (ledgerViewMode === 'inventory') {
            setDatasetFilters((prev) => ({
                ...prev,
                status: undefined,
                transaction_type: undefined,
                account_name: undefined,
                split_account_name: undefined,
                balance_state: undefined,
                has_price_changes: undefined,
                inventory_currency: undefined,
                inventory_vendor_name: undefined,
                inventory_history_state: undefined,
                sort_by: 'most_recent_seen_at',
                sort_order: 'desc',
            }));
            setHasUserSortedDataset(false);
        }
        if (ledgerViewMode === 'accounting' || ledgerViewMode === 'ar') {
            setDatasetFilters((prev) => ({
                ...prev,
                sort_by: ledgerViewMode === 'ar' ? getApArDefaultSortBy(arSource) : 'transaction_date',
                sort_order: 'desc',
            }));
            setHasUserSortedDataset(false);
        }
    }, [ledgerViewMode, arSource]);

    useEffect(() => {
        setSelectedDatasetItem(null);
        setSelectedDatasetSource(null);
        setHasUserSortedDataset(false);
        setDatasetFilters((prev) => resetDatasetFiltersForWorkspace(prev, ledgerViewMode, accountingSource, arSource));
    }, [ledgerViewMode]);

    const isGlCodeView = ledgerViewMode === 'accounting' && accountingSource === 'gl_code';
    const activeDatasetSource: LedgerDatasetFilterParams['source'] = ledgerViewMode === 'inventory'
        ? 'inventory'
        : ledgerViewMode === 'ar'
            ? (arSource === 'ap_bills' ? 'bills' : arSource === 'ap_vendors' ? 'ap_vendors' : arSource === 'ap_ledger' ? 'ap_ledger' : arSource)
            : (accountingSource as AccountingLedgerSource);
    const { inventoryQueryReady } = useInventoryQueries({
        ledgerViewMode,
        datasetFilters,
    });

    const {
        cashData,
        isCashLoading,
        refetchCashData,
        isCashFetching,
        linkedAccountsData,
        cashQboAccounts,
    } = useCashQueries({
        ledgerViewMode,
        canFetchLedgerData,
        cashFilters,
        scopeBusinessId: selectedBusinessId || activeBusinessId || undefined,
    });

    const {
        datasetData,
        isDatasetLoading,
        refetchDatasetData,
        isDatasetFetching,
        datasetFilterOptions,
    } = useLedgerDatasetQueries({
        activeDatasetSource,
        datasetFilters,
        ledgerViewMode,
        canFetchLedgerData,
        isGlCodeView,
        inventoryQueryReady,
        selectedBusinessId,
    });

    useEffect(() => {
        const catalogId = String(deepLinkCatalogId || '').trim();
        if (!catalogId || ledgerViewMode !== 'inventory' || isDatasetLoading) {
            return;
        }
        if (openedInventoryCatalogDeepLinkRef.current === catalogId) {
            return;
        }
        const items = (datasetData?.items || []) as LedgerDatasetItem[];
        const matchedItem = items.find((item) => {
            const directIds = [
                String((item as Record<string, unknown>)._id || ''),
                String((item as Record<string, unknown>).item_key || ''),
                String((item as Record<string, unknown>).catalog_id || ''),
            ];
            if (directIds.some((value) => value === catalogId)) {
                return true;
            }
            const itemRecords = ((item as Record<string, unknown>).item_records || []) as Array<Record<string, unknown>>;
            return itemRecords.some((record) => String(record._id || '') === catalogId);
        });
        if (matchedItem) {
            setSelectedDatasetItem(matchedItem);
            setSelectedDatasetSource('inventory');
            openedInventoryCatalogDeepLinkRef.current = catalogId;
        }
    }, [datasetData?.items, deepLinkCatalogId, isDatasetLoading, ledgerViewMode]);

    const {
        latestArWeeklyMetric,
        arWeeklyActions,
        arWeeklyRevenueContributions,
    } = useARQueries({
        ledgerViewMode,
        arSource,
        selectedBusinessId,
    });

    const {
        deepLinkedBill,
        isGlCodeQboLoading,
        isGlCodeStandardAccountsLoading,
        updateGlCodeMappingMutation,
        syncAccountingQboMutation,
        glCodeStandardAccounts,
        glCodeQboTypeOptions,
        glCodeClassificationOptions,
        glCodeFinzCategoryOptions,
        paginatedGlCodeAccounts,
    } = useAccountingQueries({
        deepLinkQboBillId,
        selectedBusinessId,
        isGlCodeView,
        datasetFilters,
        glCodeQboType,
        glCodeClassification,
        glCodeFinzCategory,
    });

    useEffect(() => {
        if (!deepLinkQboBillId || !deepLinkedBill) {
            return;
        }
        setSelectedDatasetItem(deepLinkedBill);
        setSelectedDatasetSource('bills');
    }, [deepLinkQboBillId, deepLinkedBill]);
    useEffect(() => {
        if (!cashFilters.qbo_account_id) {
            return;
        }
        const existsInBusiness = cashQboAccounts.some(
            (account) => account.data.account_id === cashFilters.qbo_account_id
        );
        if (!existsInBusiness) {
            setCashFilters((prev) => ({ ...prev, qbo_account_id: undefined, page: 1 }));
        }
    }, [cashFilters.qbo_account_id, cashQboAccounts]);

    const handleBatchApprove = async () => {
        if (selectedIds.length === 0) return;

        setIsBatchUpdating(true);
        try {
            await transactionApi.batchUpdateTransactions(
                selectedIds,
                { resolution_status: 'verified' },
                cashFilters.business_id
            );
            setSelectedIds([]);
            refetchCashData();
        } catch (error) {
            console.error('Failed to batch approve:', error);
        } finally {
            setIsBatchUpdating(false);
        }
    };

    const handleCashFilterChange = (newFilters: Partial<TransactionFilterParams>) => {
        setAgentAppliedFilters([]);
        setCashFilters((prev) => ({ ...prev, ...newFilters, page: 1 }));
    };

    const handleDatasetFilterChange = (newFilters: Partial<Omit<LedgerDatasetFilterParams, 'source'>>) => {
        setDatasetFilters((prev) => ({ ...prev, ...newFilters, page: 1 }));
    };

    const handleCashPageChange = (newPage: number) => {
        setCashFilters((prev) => ({ ...prev, page: newPage }));
    };

    const handleDatasetPageChange = (newPage: number) => {
        setDatasetFilters((prev) => ({ ...prev, page: newPage }));
    };

    const handleSort = (column: string) => {
        setCashFilters((prev) => {
            const isAsc = prev.sort_by === column && prev.sort_order === 'asc';
            return {
                ...prev,
                sort_by: column,
                sort_order: isAsc ? 'desc' : 'asc',
                page: 1,
            };
        });
    };

    const handleSharedDateChange = (update: [Date | null, Date | null]) => {
        const [start, end] = update;
        const startDate = formatLocalDateInput(start);
        const endDate = formatLocalDateInput(end);

        setCashFilters((prev) => ({ ...prev, start_date: startDate, end_date: endDate, page: 1 }));
        setDatasetFilters((prev) => ({ ...prev, start_date: startDate, end_date: endDate, page: 1 }));
    };

    const handleClearFilters = () => {
        setAgentAppliedFilters([]);
        setCashFilters((prev) => ({
            page: 1,
            page_size: 50,
            financial_account_id: initialAccountId || undefined,
            business_id: prev.business_id,
        }));
        setDatasetFilters((prev) => ({
            page: 1,
            page_size: 50,
            status: undefined,
            transaction_type: undefined,
            record_type: undefined,
            account_name: undefined,
            split_account_name: undefined,
            balance_state: undefined,
            has_price_changes: undefined,
            inventory_currency: undefined,
            inventory_vendor_name: undefined,
            inventory_history_state: undefined,
            due_this_week: undefined,
            overdue_only: undefined,
            critical_only: undefined,
            unapplied_cash: undefined,
            sort_by: ledgerViewMode === 'inventory' ? 'most_recent_seen_at' : ledgerViewMode === 'ar' ? getApArDefaultSortBy(arSource) : 'transaction_date',
            sort_order: ledgerViewMode === 'ar' ? getApArDefaultSortOrder(arSource) : 'desc',
            business_id: prev.business_id,
        }));
        setSelectedDatasetItem(null);
        setSelectedDatasetSource(null);
        setGlCodeQboType('');
        setGlCodeClassification('');
        setGlCodeFinzCategory('');

        if (ledgerViewMode === 'accounting' && accountingSource !== 'gl_code') {
            setAccountingSource('transaction_list');
        }
    };

    const handleApplyAgentResultSet = (payload: { filters?: AgentFilterChip[]; strictVendorMatch?: boolean }) => {
        const filters = (payload.filters || []).filter((item) => item?.label && item?.value);
        if (filters.length === 0) return;

        const nextFilters: TransactionFilterParams = {
            business_id: selectedBusinessId || activeBusinessId || undefined,
            page: 1,
            page_size: cashFilters.page_size || 50,
            sort_by: cashFilters.sort_by,
            sort_order: cashFilters.sort_order,
            financial_account_id: initialAccountId || undefined,
        };

        filters.forEach((filter) => {
            const label = String(filter.label || '').trim().toLowerCase();
            const value = String(filter.value || '').trim();
            if (!value) return;
            if (label === 'vendor') {
                nextFilters.vendor_name = value;
                nextFilters.vendor_exact = Boolean(payload.strictVendorMatch);
                return;
            }
            if (label === 'normalized description') {
                nextFilters.normalized_description = value;
                return;
            }
            if (label === 'account') {
                nextFilters.finz_account_name = value;
                return;
            }
            if (label === 'bucket') {
                nextFilters.fiscal_bucket = [value];
                return;
            }
            if (label === 'not bucket') {
                nextFilters.exclude_fiscal_bucket = [value];
                return;
            }
            if (label === 'status') {
                nextFilters.resolution_status = value;
                return;
            }
            if (label === 'not status') {
                nextFilters.exclude_resolution_status = value;
                return;
            }
            if (label === 'direction' && (value === 'inflow' || value === 'outflow')) {
                nextFilters.direction = value;
                return;
            }
            if (label === 'source') {
                nextFilters.source = [value];
                return;
            }
            if (label === 'search') {
                nextFilters.search = value;
                return;
            }
            if (label === 'date') {
                Object.assign(nextFilters, parseAgentDateFilter(value));
                return;
            }
            if (label === 'amount') {
                Object.assign(nextFilters, parseAgentAmountFilter(value));
            }
        });

        setLedgerViewMode('cash');
        setSelectedTransactionId(null);
        setSelectedIds([]);
        setAgentAppliedFilters(filters);
        const shouldForceRefresh = areTransactionFiltersEqual(cashFilters, nextFilters);
        setCashFilters(nextFilters);
        if (shouldForceRefresh) {
            void refetchCashData();
        }
    };

    const hasActiveCashFilters =
        !!cashFilters.vendor_name ||
        !!cashFilters.finz_account_name ||
        (cashFilters.fiscal_bucket && cashFilters.fiscal_bucket.length > 0) ||
        (cashFilters.exclude_fiscal_bucket && cashFilters.exclude_fiscal_bucket.length > 0) ||
        !!cashFilters.financial_account_id ||
        !!cashFilters.qbo_account_id ||
        !!cashFilters.direction ||
        !!cashFilters.start_date ||
        !!cashFilters.end_date ||
        cashFilters.operating_flag !== undefined ||
        (cashFilters.resolution_status !== undefined && cashFilters.resolution_status !== 'all') ||
        (cashFilters.exclude_resolution_status !== undefined && cashFilters.exclude_resolution_status !== 'all') ||
        cashFilters.amount !== undefined ||
        cashFilters.amount_gt !== undefined ||
        cashFilters.amount_gte !== undefined ||
        cashFilters.amount_lt !== undefined ||
        cashFilters.amount_lte !== undefined;

    const hasActiveDatasetFilters = !isGlCodeView && (
        !!datasetFilters.search ||
        !!datasetFilters.start_date ||
        !!datasetFilters.end_date ||
        !!datasetFilters.status ||
        !!datasetFilters.transaction_type ||
        !!datasetFilters.record_type ||
        !!datasetFilters.account_name ||
        !!datasetFilters.split_account_name ||
        !!datasetFilters.balance_state ||
        datasetFilters.has_price_changes !== undefined ||
        datasetFilters.due_this_week !== undefined ||
        datasetFilters.overdue_only !== undefined ||
        datasetFilters.critical_only !== undefined ||
        datasetFilters.unapplied_cash !== undefined ||
        !!datasetFilters.inventory_currency ||
        !!datasetFilters.inventory_vendor_name ||
        !!datasetFilters.inventory_history_state ||
        ((ledgerViewMode === 'accounting' && accountingSource !== 'transaction_list') || ledgerViewMode === 'ar')
    );

    const sharedStartDate = cashFilters.start_date || datasetFilters.start_date;
    const sharedEndDate = cashFilters.end_date || datasetFilters.end_date;
    const isFetching = ledgerViewMode === 'cash' ? isCashFetching : (isGlCodeView ? false : isDatasetFetching);
    const invalidateLedgerQueries = async (businessId?: string) => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['ledger-dataset'] }),
            queryClient.invalidateQueries({ queryKey: ['ledger-filter-options'] }),
            queryClient.invalidateQueries({ queryKey: ['ledger-qbo-bill'] }),
            queryClient.invalidateQueries({ queryKey: ['ledger-vendor-directory'] }),
            queryClient.invalidateQueries({ queryKey: ['ap-vendor-detail'] }),
            queryClient.invalidateQueries({ queryKey: ['qboAccounts', businessId] }),
            queryClient.invalidateQueries({ queryKey: ['cash-qbo-accounts', businessId] }),
            queryClient.invalidateQueries({ queryKey: ['standardAccounts', businessId] }),
            queryClient.invalidateQueries({ queryKey: ['ar-customer-detail'] }),
            queryClient.invalidateQueries({ queryKey: ['ar-ledger-detail'] }),
            queryClient.invalidateQueries({ queryKey: ['ar-open-invoices'] }),
            queryClient.invalidateQueries({ queryKey: ['ar-weekly-metrics', businessId] }),
            queryClient.invalidateQueries({ queryKey: ['ar-weekly-actions', businessId] }),
            queryClient.invalidateQueries({ queryKey: ['ar-weekly-revenue-contributions', businessId] }),
            queryClient.invalidateQueries({ queryKey: ['signals'] }),
            queryClient.invalidateQueries({ queryKey: ['signal-counts'] }),
            queryClient.invalidateQueries({ queryKey: ['weekly-summary', businessId] }),
            queryClient.invalidateQueries({ queryKey: ['weekly-summary-banner', businessId] }),
        ]);
    };
    const refreshVisibleLedgerState = async () => {
        if (ledgerViewMode === 'cash') {
            await refetchCashData();
            return;
        }
        if (isGlCodeView) {
            return;
        }
        const refreshed = await refetchDatasetData();
        const nextItems = refreshed.data?.items || [];
        if (selectedDatasetSource === activeDatasetSource && selectedDatasetItem && nextItems.length > 0) {
            const matchedItem = findMatchingDatasetItem(nextItems as LedgerDatasetItem[], selectedDatasetItem);
            if (matchedItem) {
                setSelectedDatasetItem(matchedItem);
            }
        }
    };
    const handleRefresh = async () => {
        if (ledgerViewMode === 'cash') {
            return refetchCashData();
        }
        if (!selectedBusinessId) {
            return Promise.resolve();
        }
        setIsRefreshSettling(true);
        try {
            await syncAccountingQboMutation.mutateAsync(selectedBusinessId);
            await invalidateLedgerQueries(selectedBusinessId);
            if (isGlCodeView) {
                return Promise.resolve();
            }
            await refreshVisibleLedgerState();
            for (let attempt = 0; attempt < REFRESH_SETTLE_POLL_ATTEMPTS; attempt += 1) {
                await sleep(REFRESH_SETTLE_POLL_DELAY_MS);
                await invalidateLedgerQueries(selectedBusinessId);
                await refreshVisibleLedgerState();
            }
            return Promise.resolve();
        } finally {
            setIsRefreshSettling(false);
        }
    };
    const drawerIsUploadedSource = selectedDatasetSource === 'uploaded_invoices' || selectedDatasetSource === 'uploaded_expenses' || selectedDatasetSource === 'uploaded_bills';

    const handleDatasetRowClick = (item: LedgerDatasetItem) => {
        setSelectedDatasetItem(item);
        setSelectedDatasetSource(activeDatasetSource);
    };

    const handleDatasetSort = (column: string) => {
        setHasUserSortedDataset(true);
        setDatasetFilters((prev) => {
            const isAsc = prev.sort_by === column && prev.sort_order === 'asc';
            return {
                ...prev,
                sort_by: column,
                sort_order: isAsc ? 'desc' : 'asc',
                page: 1,
            };
        });
    };

    const handleOpenLinkedRecord = async (invoiceId: string, preferredSource?: 'uploaded_invoices' | 'uploaded_expenses' | 'uploaded_bills' | null) => {
        const targetBusinessId = datasetFilters.business_id || selectedBusinessId || activeBusinessId || undefined;
        try {
            const resolved = await ledgerViewsApi.getInventoryLinkedDocument(invoiceId, {
                business_id: targetBusinessId,
                preferred_source: preferredSource || undefined,
            });
            setSelectedDatasetItem(resolved.item as unknown as LedgerDatasetItem);
            setSelectedDatasetSource(resolved.source);
        } catch (error) {
            console.error('Failed to open linked inventory document:', error);
        }
    };

    const handleOpenArInvoiceFromAr = async (invoiceId: string) => {
        const targetBusinessId = datasetFilters.business_id || selectedBusinessId || activeBusinessId || undefined;
        try {
            const payload = await ledgerViewsApi.getLedgerItems({
                source: 'ar_ledger',
                business_id: targetBusinessId,
                search: invoiceId,
                page: 1,
                page_size: 50,
                sort_by: 'event_date',
                sort_order: 'desc',
            });
            const items = payload.items || [];
            const invoiceRecord = items.find((item) => (
                String((item as Record<string, unknown>).record_type || '').trim() === 'invoice'
                && String((item as Record<string, unknown>).source_id || '').trim() === invoiceId
            )) || items.find((item) => (
                String((item as Record<string, unknown>).record_type || '').trim() === 'invoice'
            )) || items[0];
            if (!invoiceRecord) return;
            setLedgerViewMode('ar');
            setArSource('ar_ledger');
            setHasUserSortedDataset(false);
            setSelectedDatasetSource('ar_ledger');
            setSelectedDatasetItem(null);
            setDatasetFilters((prev) => ({
                ...prev,
                business_id: targetBusinessId,
                page: 1,
                search: invoiceId,
                due_this_week: undefined,
                unapplied_cash: undefined,
                sort_by: 'event_date',
                sort_order: 'desc',
            }));
            window.setTimeout(() => {
                setSelectedDatasetItem(invoiceRecord);
            }, 0);
        } catch (error) {
            console.error('Failed to open AR invoice record from AR drawer:', error);
        }
    };

    const handleOpenApBillFromVendor = async (billId: string) => {
        const targetBusinessId = datasetFilters.business_id || selectedBusinessId || activeBusinessId || undefined;
        try {
            const bill = await ledgerViewsApi.getQboBillById(billId, targetBusinessId);
            setLedgerViewMode('ar');
            setArSource('ap_ledger');
            setHasUserSortedDataset(false);
            setSelectedDatasetSource('bills');
            setSelectedDatasetItem(bill as unknown as LedgerDatasetItem);
            setDatasetFilters((prev) => ({
                ...prev,
                business_id: targetBusinessId,
                page: 1,
                search: bill.data?.bill_id || bill.data?.vendor?.name || billId,
                status: undefined,
                record_type: undefined,
                balance_state: undefined,
                due_this_week: undefined,
                overdue_only: undefined,
                critical_only: undefined,
                sort_by: getApArDefaultSortBy('ap_ledger'),
                sort_order: getApArDefaultSortOrder('ap_ledger'),
            }));
        } catch (error) {
            console.error('Failed to open AP bill from vendor drawer:', error);
        }
    };

    const handleOpenQboTransactionFromAr = async (transactionId: string) => {
        const targetBusinessId = datasetFilters.business_id || selectedBusinessId || activeBusinessId || undefined;
        try {
            const transaction = await ledgerViewsApi.getQboTransactionById(transactionId, targetBusinessId);
            setLedgerViewMode('accounting');
            setAccountingSource('transaction_list');
            setHasUserSortedDataset(false);
            setSelectedDatasetItem(transaction as unknown as LedgerDatasetItem);
            setSelectedDatasetSource('transaction_list');
            setDatasetFilters((prev) => ({
                ...prev,
                business_id: targetBusinessId,
                page: 1,
                search: transaction.data?.customer?.name || transaction.data?.document_number || transactionId,
                status: undefined,
                transaction_type: undefined,
                account_name: undefined,
                split_account_name: undefined,
                balance_state: undefined,
                has_price_changes: undefined,
                inventory_currency: undefined,
                inventory_vendor_name: undefined,
                inventory_history_state: undefined,
                sort_by: 'transaction_date',
                sort_order: 'desc',
            }));
        } catch (error) {
            console.error('Failed to open QBO transaction from AR drawer:', error);
        }
    };

    const handleOpenArCustomer = (customerId: string, customerName?: string) => {
        const targetBusinessId = datasetFilters.business_id || selectedBusinessId || activeBusinessId || undefined;
        setLedgerViewMode('ar');
        setArSource('ar_customers');
        setHasUserSortedDataset(false);
        setSelectedDatasetItem({
            customer_id: customerId,
            customer_name: customerName || 'Customer',
            business_id: targetBusinessId,
        } as LedgerDatasetItem);
        setSelectedDatasetSource('ar_customers');
        setDatasetFilters((prev) => ({
            ...prev,
            business_id: targetBusinessId,
            page: 1,
            search: customerName || prev.search,
            balance_state: undefined,
            record_type: undefined,
            due_this_week: undefined,
            unapplied_cash: undefined,
            sort_by: 'overdue_ar',
            sort_order: 'desc',
        }));
    };

    const handleOpenCashTransactionFromAr = (transactionId: string) => {
        if (!transactionId) return;
        setLedgerViewMode('cash');
        setSelectedTransactionId(transactionId);
    };

    const handleOpenArPaymentFromCustomer = async (paymentId: string) => {
        const targetBusinessId = datasetFilters.business_id || selectedBusinessId || activeBusinessId || undefined;
        if (!paymentId) return;
        try {
            const payload = await ledgerViewsApi.getLedgerItems({
                source: 'ar_ledger',
                business_id: targetBusinessId,
                search: paymentId,
                page: 1,
                page_size: 50,
                sort_by: 'event_date',
                sort_order: 'desc',
            });
            const items = payload.items || [];
            const paymentRecord = items.find((item) => (
                String((item as Record<string, unknown>).record_type || '').trim() === 'payment'
                && String((item as Record<string, unknown>).source_id || '').trim() === paymentId
            )) || items.find((item) => (
                String((item as Record<string, unknown>).record_type || '').trim() === 'payment'
            )) || items[0];
            if (!paymentRecord) return;
            setLedgerViewMode('ar');
            setArSource('ar_ledger');
            setHasUserSortedDataset(false);
            setSelectedDatasetSource('ar_ledger');
            setSelectedDatasetItem(null);
            setDatasetFilters((prev) => ({
                ...prev,
                business_id: targetBusinessId,
                page: 1,
                search: paymentId,
                due_this_week: undefined,
                unapplied_cash: undefined,
                sort_by: 'event_date',
                sort_order: 'desc',
            }));
            window.setTimeout(() => {
                setSelectedDatasetItem(paymentRecord);
            }, 0);
        } catch (error) {
            console.error('Failed to open AR payment record from customer drawer:', error);
        }
    };

    const handleOpenDocumentFile = (fileId: string, source: 'uploaded_invoices' | 'uploaded_expenses' | 'uploaded_bills') => {
        if (!fileId) return;
        const targetBusinessId = datasetFilters.business_id || selectedBusinessId || activeBusinessId || undefined;
        const params = new URLSearchParams();
        params.set('target_file_id', fileId);
        params.set('target_doc_type', source === 'uploaded_invoices' ? 'invoice' : source === 'uploaded_expenses' ? 'expenses' : 'bills');
        if (targetBusinessId) {
            params.set('target_business_id', targetBusinessId);
        }
        navigate(`/drive?${params.toString()}`);
    };

    const handleShowChat = () => {
        setShowChat(true);
    };

    const accountingFilterPanel = (
        ledgerViewMode === 'inventory' ? (
            <InventoryFilters
                searchValue={datasetFilters.search || ''}
                onSearchChange={(value) => handleDatasetFilterChange({ search: value })}
                hideSearch={isMobile}
                accountingSource="transaction_list"
                onAccountingSourceChange={() => undefined}
                arSource={arSource}
                onArSourceChange={() => undefined}
                statusValue={datasetFilters.status || ''}
                onStatusChange={(value) => handleDatasetFilterChange({ status: value || undefined })}
                transactionTypeValue={datasetFilters.transaction_type || ''}
                onTransactionTypeChange={(value) => handleDatasetFilterChange({ transaction_type: value || undefined })}
                recordTypeValue={datasetFilters.record_type || ''}
                onRecordTypeChange={(value) => handleDatasetFilterChange({ record_type: value || undefined })}
                accountNameValue={datasetFilters.account_name || ''}
                onAccountNameChange={(value) => handleDatasetFilterChange({ account_name: value || undefined })}
                splitAccountNameValue={datasetFilters.split_account_name || ''}
                onSplitAccountNameChange={(value) => handleDatasetFilterChange({ split_account_name: value || undefined })}
                balanceStateValue={datasetFilters.balance_state || ''}
                onBalanceStateChange={(value) => handleDatasetFilterChange({ balance_state: (value || undefined) as 'open' | 'closed' | undefined })}
                priceChangeValue={datasetFilters.has_price_changes === undefined ? '' : datasetFilters.has_price_changes ? 'changed' : 'unchanged'}
                onPriceChangeChange={(value) => handleDatasetFilterChange({
                    has_price_changes: value === '' ? undefined : value === 'changed'
                })}
                inventoryCurrencyValue={datasetFilters.inventory_currency || ''}
                onInventoryCurrencyChange={(value) => handleDatasetFilterChange({ inventory_currency: value || undefined })}
                inventoryVendorValue={datasetFilters.inventory_vendor_name || ''}
                onInventoryVendorChange={(value) => handleDatasetFilterChange({ inventory_vendor_name: value || undefined })}
                inventoryHistoryValue={datasetFilters.inventory_history_state || ''}
                onInventoryHistoryChange={(value) => handleDatasetFilterChange({ inventory_history_state: (value || undefined) as 'repeat' | 'single' | 'changed' | 'stable' | undefined })}
                accountOptions={datasetFilterOptions?.account_names || []}
                splitAccountOptions={datasetFilterOptions?.split_account_names || []}
                inventoryCurrencyOptions={datasetFilterOptions?.inventory_currencies || []}
                inventoryVendorOptions={datasetFilterOptions?.inventory_vendors || []}
                glCodeQboTypeValue={glCodeQboType}
                onGlCodeQboTypeChange={setGlCodeQboType}
                glCodeClassificationValue={glCodeClassification}
                onGlCodeClassificationChange={setGlCodeClassification}
                glCodeFinzCategoryValue={glCodeFinzCategory}
                onGlCodeFinzCategoryChange={setGlCodeFinzCategory}
                glCodeQboTypeOptions={glCodeQboTypeOptions}
                glCodeClassificationOptions={glCodeClassificationOptions}
                glCodeFinzCategoryOptions={glCodeFinzCategoryOptions}
                onClearFilters={handleClearFilters}
                onOpenQboVendorList={() => setVendorDirectorySource('qbo')}
                qboDirectoryLabel="QBO Vendors"
                onOpenArWeeklySummary={() => setIsArWeeklySummaryOpen(true)}
                arWeeklySummaryLabel="AR Weekly Summary"
                onOpenFinzAccountList={() => setIsGlCodeFinzSelectorOpen(true)}
                dueThisWeekValue={!!datasetFilters.due_this_week}
                onDueThisWeekChange={(value) => handleDatasetFilterChange({ due_this_week: value || undefined })}
                overdueOnlyValue={false}
                onOverdueOnlyChange={() => undefined}
                criticalOnlyValue={false}
                onCriticalOnlyChange={() => undefined}
                unappliedCashValue={false}
                onUnappliedCashChange={() => undefined}
            />
        ) : ledgerViewMode === 'ar' && isApWorkspaceSource(arSource) ? (
            <APFilters
                searchValue={datasetFilters.search || ''}
                onSearchChange={(value) => handleDatasetFilterChange({ search: value })}
                hideSearch={isMobile}
                accountingSource="transaction_list"
                onAccountingSourceChange={() => undefined}
                arSource={arSource}
                onArSourceChange={(value) => {
                    setArSource(value);
                    setSelectedDatasetItem(null);
                    setSelectedDatasetSource(null);
                    setHasUserSortedDataset(false);
                    setDatasetFilters((prev) => ({
                        ...prev,
                        page: 1,
                        status: undefined,
                        transaction_type: undefined,
                        account_name: undefined,
                        split_account_name: undefined,
                        balance_state: undefined,
                        has_price_changes: undefined,
                        inventory_currency: undefined,
                        inventory_vendor_name: undefined,
                        inventory_history_state: undefined,
                        record_type: undefined,
                        due_this_week: isApWorkspaceSource(value) ? undefined : prev.due_this_week,
                        overdue_only: isApWorkspaceSource(value) ? prev.overdue_only : undefined,
                        critical_only: isApWorkspaceSource(value) ? prev.critical_only : undefined,
                        unapplied_cash: isApWorkspaceSource(value) ? undefined : prev.unapplied_cash,
                        sort_by: getApArDefaultSortBy(value),
                        sort_order: getApArDefaultSortOrder(value),
                    }));
                }}
                statusValue={datasetFilters.status || ''}
                onStatusChange={(value) => handleDatasetFilterChange({ status: value || undefined })}
                transactionTypeValue={datasetFilters.transaction_type || ''}
                onTransactionTypeChange={(value) => handleDatasetFilterChange({ transaction_type: value || undefined })}
                recordTypeValue={datasetFilters.record_type || ''}
                onRecordTypeChange={(value) => handleDatasetFilterChange({ record_type: value || undefined })}
                accountNameValue={datasetFilters.account_name || ''}
                onAccountNameChange={(value) => handleDatasetFilterChange({ account_name: value || undefined })}
                splitAccountNameValue={datasetFilters.split_account_name || ''}
                onSplitAccountNameChange={(value) => handleDatasetFilterChange({ split_account_name: value || undefined })}
                balanceStateValue={datasetFilters.balance_state || ''}
                onBalanceStateChange={(value) => handleDatasetFilterChange({ balance_state: (value || undefined) as 'open' | 'closed' | undefined })}
                priceChangeValue={datasetFilters.has_price_changes === undefined ? '' : datasetFilters.has_price_changes ? 'changed' : 'unchanged'}
                onPriceChangeChange={(value) => handleDatasetFilterChange({
                    has_price_changes: value === '' ? undefined : value === 'changed'
                })}
                inventoryCurrencyValue={datasetFilters.inventory_currency || ''}
                onInventoryCurrencyChange={(value) => handleDatasetFilterChange({ inventory_currency: value || undefined })}
                inventoryVendorValue={datasetFilters.inventory_vendor_name || ''}
                onInventoryVendorChange={(value) => handleDatasetFilterChange({ inventory_vendor_name: value || undefined })}
                inventoryHistoryValue={datasetFilters.inventory_history_state || ''}
                onInventoryHistoryChange={(value) => handleDatasetFilterChange({ inventory_history_state: (value || undefined) as 'repeat' | 'single' | 'changed' | 'stable' | undefined })}
                accountOptions={datasetFilterOptions?.account_names || []}
                splitAccountOptions={datasetFilterOptions?.split_account_names || []}
                inventoryCurrencyOptions={datasetFilterOptions?.inventory_currencies || []}
                inventoryVendorOptions={datasetFilterOptions?.inventory_vendors || []}
                glCodeQboTypeValue={glCodeQboType}
                onGlCodeQboTypeChange={setGlCodeQboType}
                glCodeClassificationValue={glCodeClassification}
                onGlCodeClassificationChange={setGlCodeClassification}
                glCodeFinzCategoryValue={glCodeFinzCategory}
                onGlCodeFinzCategoryChange={setGlCodeFinzCategory}
                glCodeQboTypeOptions={glCodeQboTypeOptions}
                glCodeClassificationOptions={glCodeClassificationOptions}
                glCodeFinzCategoryOptions={glCodeFinzCategoryOptions}
                onClearFilters={handleClearFilters}
                onOpenQboVendorList={() => setVendorDirectorySource('qbo')}
                qboDirectoryLabel="QBO Vendors"
                onOpenArWeeklySummary={() => setIsUnifiedWeeklySummaryOpen(true)}
                arWeeklySummaryLabel="AP Weekly Summary"
                onOpenFinzAccountList={() => setIsGlCodeFinzSelectorOpen(true)}
                dueThisWeekValue={!!datasetFilters.due_this_week}
                onDueThisWeekChange={(value) => handleDatasetFilterChange({
                    due_this_week: value || undefined,
                    ...(value ? { overdue_only: undefined } : {}),
                })}
                overdueOnlyValue={!!datasetFilters.overdue_only}
                onOverdueOnlyChange={(value) => handleDatasetFilterChange({
                    overdue_only: value || undefined,
                    ...(value ? { due_this_week: undefined } : {}),
                })}
                criticalOnlyValue={!!datasetFilters.critical_only}
                onCriticalOnlyChange={(value) => handleDatasetFilterChange({ critical_only: value || undefined })}
                unappliedCashValue={false}
                onUnappliedCashChange={() => undefined}
            />
        ) : ledgerViewMode === 'ar' ? (
            <ARFilters
                searchValue={datasetFilters.search || ''}
                onSearchChange={(value) => handleDatasetFilterChange({ search: value })}
                hideSearch={isMobile}
                accountingSource="transaction_list"
                onAccountingSourceChange={() => undefined}
                arSource={arSource}
                onArSourceChange={(value) => {
                    setArSource(value);
                    setSelectedDatasetItem(null);
                    setSelectedDatasetSource(null);
                    setHasUserSortedDataset(false);
                    setDatasetFilters((prev) => ({
                        ...prev,
                        page: 1,
                        status: undefined,
                        transaction_type: undefined,
                        account_name: undefined,
                        split_account_name: undefined,
                        balance_state: undefined,
                        has_price_changes: undefined,
                        inventory_currency: undefined,
                        inventory_vendor_name: undefined,
                        inventory_history_state: undefined,
                        record_type: undefined,
                        due_this_week: isApWorkspaceSource(value) ? undefined : prev.due_this_week,
                        overdue_only: isApWorkspaceSource(value) ? prev.overdue_only : undefined,
                        critical_only: isApWorkspaceSource(value) ? prev.critical_only : undefined,
                        unapplied_cash: isApWorkspaceSource(value) ? undefined : prev.unapplied_cash,
                        sort_by: getApArDefaultSortBy(value),
                        sort_order: getApArDefaultSortOrder(value),
                    }));
                }}
                statusValue={datasetFilters.status || ''}
                onStatusChange={(value) => handleDatasetFilterChange({ status: value || undefined })}
                transactionTypeValue={datasetFilters.transaction_type || ''}
                onTransactionTypeChange={(value) => handleDatasetFilterChange({ transaction_type: value || undefined })}
                recordTypeValue={datasetFilters.record_type || ''}
                onRecordTypeChange={(value) => handleDatasetFilterChange({ record_type: value || undefined })}
                accountNameValue={datasetFilters.account_name || ''}
                onAccountNameChange={(value) => handleDatasetFilterChange({ account_name: value || undefined })}
                splitAccountNameValue={datasetFilters.split_account_name || ''}
                onSplitAccountNameChange={(value) => handleDatasetFilterChange({ split_account_name: value || undefined })}
                balanceStateValue={datasetFilters.balance_state || ''}
                onBalanceStateChange={(value) => handleDatasetFilterChange({ balance_state: (value || undefined) as 'open' | 'closed' | undefined })}
                priceChangeValue={datasetFilters.has_price_changes === undefined ? '' : datasetFilters.has_price_changes ? 'changed' : 'unchanged'}
                onPriceChangeChange={(value) => handleDatasetFilterChange({
                    has_price_changes: value === '' ? undefined : value === 'changed'
                })}
                inventoryCurrencyValue={datasetFilters.inventory_currency || ''}
                onInventoryCurrencyChange={(value) => handleDatasetFilterChange({ inventory_currency: value || undefined })}
                inventoryVendorValue={datasetFilters.inventory_vendor_name || ''}
                onInventoryVendorChange={(value) => handleDatasetFilterChange({ inventory_vendor_name: value || undefined })}
                inventoryHistoryValue={datasetFilters.inventory_history_state || ''}
                onInventoryHistoryChange={(value) => handleDatasetFilterChange({ inventory_history_state: (value || undefined) as 'repeat' | 'single' | 'changed' | 'stable' | undefined })}
                accountOptions={datasetFilterOptions?.account_names || []}
                splitAccountOptions={datasetFilterOptions?.split_account_names || []}
                inventoryCurrencyOptions={datasetFilterOptions?.inventory_currencies || []}
                inventoryVendorOptions={datasetFilterOptions?.inventory_vendors || []}
                glCodeQboTypeValue={glCodeQboType}
                onGlCodeQboTypeChange={setGlCodeQboType}
                glCodeClassificationValue={glCodeClassification}
                onGlCodeClassificationChange={setGlCodeClassification}
                glCodeFinzCategoryValue={glCodeFinzCategory}
                onGlCodeFinzCategoryChange={setGlCodeFinzCategory}
                glCodeQboTypeOptions={glCodeQboTypeOptions}
                glCodeClassificationOptions={glCodeClassificationOptions}
                glCodeFinzCategoryOptions={glCodeFinzCategoryOptions}
                onClearFilters={handleClearFilters}
                onOpenQboVendorList={() => setVendorDirectorySource('qbo_customers')}
                qboDirectoryLabel="QBO Customers"
                onOpenArWeeklySummary={() => setIsArWeeklySummaryOpen(true)}
                arWeeklySummaryLabel="AR Weekly Summary"
                onOpenFinzAccountList={() => setIsGlCodeFinzSelectorOpen(true)}
                dueThisWeekValue={!!datasetFilters.due_this_week}
                onDueThisWeekChange={(value) => handleDatasetFilterChange({ due_this_week: value || undefined })}
                overdueOnlyValue={false}
                onOverdueOnlyChange={() => undefined}
                criticalOnlyValue={false}
                onCriticalOnlyChange={() => undefined}
                unappliedCashValue={datasetFilters.unapplied_cash}
                onUnappliedCashChange={(value) => handleDatasetFilterChange({ unapplied_cash: value || undefined })}
            />
        ) : (
            <AccountingFilters
                searchValue={datasetFilters.search || ''}
                onSearchChange={(value) => handleDatasetFilterChange({ search: value })}
                hideSearch={isMobile}
                accountingSource={accountingSource}
                onAccountingSourceChange={(value) => {
                    setAccountingSource(value);
                    setSelectedDatasetItem(null);
                    setSelectedDatasetSource(null);
                    setHasUserSortedDataset(false);
                    if (value !== 'gl_code') {
                        setDatasetFilters((prev) => ({
                            ...prev,
                            page: 1,
                            status: undefined,
                            transaction_type: undefined,
                            account_name: undefined,
                            split_account_name: undefined,
                            balance_state: undefined,
                            has_price_changes: undefined,
                            inventory_currency: undefined,
                            inventory_vendor_name: undefined,
                            inventory_history_state: undefined,
                            sort_by: 'transaction_date',
                            sort_order: 'desc',
                        }));
                    }
                }}
                arSource={arSource}
                onArSourceChange={() => undefined}
                statusValue={datasetFilters.status || ''}
                onStatusChange={(value) => handleDatasetFilterChange({ status: value || undefined })}
                transactionTypeValue={datasetFilters.transaction_type || ''}
                onTransactionTypeChange={(value) => handleDatasetFilterChange({ transaction_type: value || undefined })}
                recordTypeValue={datasetFilters.record_type || ''}
                onRecordTypeChange={(value) => handleDatasetFilterChange({ record_type: value || undefined })}
                accountNameValue={datasetFilters.account_name || ''}
                onAccountNameChange={(value) => handleDatasetFilterChange({ account_name: value || undefined })}
                splitAccountNameValue={datasetFilters.split_account_name || ''}
                onSplitAccountNameChange={(value) => handleDatasetFilterChange({ split_account_name: value || undefined })}
                balanceStateValue={datasetFilters.balance_state || ''}
                onBalanceStateChange={(value) => handleDatasetFilterChange({ balance_state: (value || undefined) as 'open' | 'closed' | undefined })}
                priceChangeValue={datasetFilters.has_price_changes === undefined ? '' : datasetFilters.has_price_changes ? 'changed' : 'unchanged'}
                onPriceChangeChange={(value) => handleDatasetFilterChange({
                    has_price_changes: value === '' ? undefined : value === 'changed'
                })}
                inventoryCurrencyValue={datasetFilters.inventory_currency || ''}
                onInventoryCurrencyChange={(value) => handleDatasetFilterChange({ inventory_currency: value || undefined })}
                inventoryVendorValue={datasetFilters.inventory_vendor_name || ''}
                onInventoryVendorChange={(value) => handleDatasetFilterChange({ inventory_vendor_name: value || undefined })}
                inventoryHistoryValue={datasetFilters.inventory_history_state || ''}
                onInventoryHistoryChange={(value) => handleDatasetFilterChange({ inventory_history_state: (value || undefined) as 'repeat' | 'single' | 'changed' | 'stable' | undefined })}
                accountOptions={datasetFilterOptions?.account_names || []}
                splitAccountOptions={datasetFilterOptions?.split_account_names || []}
                inventoryCurrencyOptions={datasetFilterOptions?.inventory_currencies || []}
                inventoryVendorOptions={datasetFilterOptions?.inventory_vendors || []}
                glCodeQboTypeValue={glCodeQboType}
                onGlCodeQboTypeChange={setGlCodeQboType}
                glCodeClassificationValue={glCodeClassification}
                onGlCodeClassificationChange={setGlCodeClassification}
                glCodeFinzCategoryValue={glCodeFinzCategory}
                onGlCodeFinzCategoryChange={setGlCodeFinzCategory}
                glCodeQboTypeOptions={glCodeQboTypeOptions}
                glCodeClassificationOptions={glCodeClassificationOptions}
                glCodeFinzCategoryOptions={glCodeFinzCategoryOptions}
                onClearFilters={handleClearFilters}
                onOpenQboVendorList={() => setVendorDirectorySource('qbo')}
                qboDirectoryLabel="QBO Vendors"
                onOpenArWeeklySummary={() => setIsArWeeklySummaryOpen(true)}
                arWeeklySummaryLabel="AR Weekly Summary"
                onOpenFinzAccountList={() => setIsGlCodeFinzSelectorOpen(true)}
                dueThisWeekValue={!!datasetFilters.due_this_week}
                onDueThisWeekChange={(value) => handleDatasetFilterChange({ due_this_week: value || undefined })}
                overdueOnlyValue={false}
                onOverdueOnlyChange={() => undefined}
                criticalOnlyValue={false}
                onCriticalOnlyChange={() => undefined}
                unappliedCashValue={false}
                onUnappliedCashChange={() => undefined}
            />
        )
    );

    const datasetTableCommonProps = {
        items: isTutorialDemo && ledgerViewMode === 'ar' && arSource === 'ar_customers'
            ? LEDGER_TUTORIAL_AR_CUSTOMERS
            : isGlCodeView
                ? paginatedGlCodeAccounts.items
                : (datasetData?.items || []),
        isLoading: isTutorialDemo && ledgerViewMode === 'ar' && arSource === 'ar_customers'
            ? false
            : isGlCodeView
                ? (isGlCodeQboLoading || isGlCodeStandardAccountsLoading)
                : isDatasetLoading,
        total: isTutorialDemo && ledgerViewMode === 'ar' && arSource === 'ar_customers'
            ? LEDGER_TUTORIAL_AR_CUSTOMERS.length
            : isGlCodeView
                ? paginatedGlCodeAccounts.total
                : (datasetData?.total || 0),
        page: datasetFilters.page || 1,
        pageSize: datasetFilters.page_size || 50,
        onPageChange: handleDatasetPageChange,
        hasActiveFilters: hasActiveDatasetFilters,
        sortBy: datasetFilters.sort_by,
        sortOrder: datasetFilters.sort_order,
        defaultSortBy: ledgerViewMode === 'inventory' ? 'most_recent_seen_at' : ledgerViewMode === 'ar' ? getApArDefaultSortBy(arSource) : 'transaction_date',
        defaultSortOrder: 'desc' as const,
        suppressDefaultSortIndicator: !hasUserSortedDataset,
        onSort: handleDatasetSort,
        onRowClick: handleDatasetRowClick,
        standardAccounts: glCodeStandardAccounts,
        businessId: selectedBusinessId || undefined,
        onUpdateGlMapping: isGlCodeView ? (qboAccountId: string, standardAccountId: string | null) => updateGlCodeMappingMutation.mutate({ qboAccountId, standardAccountId }) : undefined,
        isUpdatingGlMapping: updateGlCodeMappingMutation.isPending,
    };

    const datasetTable = ledgerViewMode === 'inventory' ? (
        <InventoryItemsTable {...datasetTableCommonProps} />
    ) : ledgerViewMode === 'ar' ? (
        arSource === 'ar_customers' ? (
            <ARCustomersTable {...datasetTableCommonProps} />
        ) : arSource === 'ar_ledger' ? (
            <ARLedgerTable {...datasetTableCommonProps} />
        ) : arSource === 'ap_vendors' ? (
            <APVendorsTable {...datasetTableCommonProps} />
        ) : (
            <APLedgerTable {...datasetTableCommonProps} />
        )
    ) : (
        <AccountingDatasetTable
            {...datasetTableCommonProps}
            accountingSource={accountingSource as Exclude<AccountingLedgerSource, 'ar_customers' | 'ar_ledger' | 'ap_vendors' | 'ap_ledger'>}
        />
    );

    const cashFilterPanel = (
        <LedgerFilters
            filters={cashFilters}
            onFilterChange={handleCashFilterChange}
            onClearFilters={handleClearFilters}
            linkedAccounts={linkedAccountsData?.linked_accounts || []}
            qboAccounts={cashQboAccounts}
            onOpenVendorManagement={() => setIsVendorManagementOpen(true)}
            onOpenVendorList={() => setVendorDirectorySource('aliases')}
            hideSearch={isMobile}
        />
    );

    const currentMobileFilterPanel = ledgerViewMode === 'cash' ? cashFilterPanel : accountingFilterPanel;
    const mobileSearchValue = ledgerViewMode === 'cash' ? (cashFilters.search || '') : (datasetFilters.search || '');
    const mobileSearchPlaceholder = ledgerViewMode === 'cash'
        ? 'Search vendor, description, amount...'
        : ledgerViewMode === 'inventory'
            ? 'Search vendor, item, or invoice ID...'
            : ledgerViewMode === 'ar'
                ? isApWorkspaceSource(arSource)
                    ? 'Search vendor, reference, status...'
                    : 'Search customer, reference, status...'
                : 'Search customer, vendor, memo, amount...';
    const handleMobileSearchChange = (value: string) => {
        if (ledgerViewMode === 'cash') {
            handleCashFilterChange({ search: value || undefined });
        } else {
            handleDatasetFilterChange({ search: value || undefined });
        }
    };

    return (
        <div className={`${isAdminEmbedded ? 'h-full' : 'h-screen'} flex overflow-hidden relative`}>
            <div className={`min-w-0 overflow-hidden flex flex-col ${showChat && !showSetupGuidePanel && !isMobile ? 'flex-1' : 'w-full'}`}>
                <div className={`min-w-0 ${isAdminEmbedded ? 'mx-0 mb-5 mt-0' : 'mx-5 mb-5 mt-[16px]'} flex min-h-0 flex-1 flex-col`}>
                    <div data-tutorial="ledger-filters" className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="flex items-center justify-between gap-2">
                            <h1 className="min-w-0 pt-0.5 text-xl font-semibold sm:text-2xl">Ledger</h1>
                            <div className="flex shrink-0 items-center gap-1 sm:hidden">
                                <div ref={mobileViewModeMenuRef} className="relative h-10 w-[88px] shrink-0">
                                    <button
                                        type="button"
                                        data-tutorial="ledger-view-select"
                                        onClick={() => setIsViewModeMenuOpen((current) => !current)}
                                        className="flex h-10 w-full items-center justify-between rounded border border-gray-300 bg-white px-2.5 text-left text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    >
                                        <span className="truncate">{LEDGER_MOBILE_VIEW_LABELS[ledgerViewMode]}</span>
                                        <ChevronDown className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${isViewModeMenuOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    {isViewModeMenuOpen && (
                                        <div
                                            data-tutorial="ledger-view-menu"
                                            className="absolute right-0 top-12 z-50 w-44 overflow-hidden rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg"
                                        >
                                            {LEDGER_VIEW_OPTIONS.map((option) => (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    onClick={() => {
                                                        setLedgerViewMode(option.value);
                                                        setIsViewModeMenuOpen(false);
                                                    }}
                                                    className={`block w-full px-3 py-2 text-left hover:bg-gray-50 ${option.value === ledgerViewMode ? 'text-gray-900' : 'text-gray-700'}`}
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div ref={cashMobileFiltersRef} className="relative h-10 w-10 shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => setIsCashMobileFiltersOpen((current) => !current)}
                                        aria-label="Filters"
                                        title="Filters"
                                        className="relative flex h-10 w-10 items-center justify-center rounded border border-gray-300 bg-white p-0 text-sm text-gray-600 shadow-sm active:bg-gray-50"
                                    >
                                        <SlidersHorizontal className="h-4 w-4" />
                                    </button>
                                    {isCashMobileFiltersOpen && (
                                        <div className="fixed left-1/2 top-20 z-50 max-h-[70vh] w-[calc(100vw-2rem)] max-w-[420px] -translate-x-1/2 overflow-auto rounded-lg border border-gray-200 bg-white p-3 shadow-xl">
                                            {currentMobileFilterPanel}
                                        </div>
                                    )}
                                </div>
                                <div className="h-10 w-10 shrink-0 [&_.react-datepicker-wrapper]:block [&_.react-datepicker-wrapper]:h-10 [&_.react-datepicker-wrapper]:w-10 [&_.react-datepicker__input-container]:block [&_.react-datepicker__input-container]:h-10 [&_.react-datepicker__input-container]:w-10">
                                    <DatePicker
                                        customInput={(
                                            <button
                                                type="button"
                                                aria-label="Select date range"
                                                title="Select date range"
                                                className="relative flex h-10 w-10 items-center justify-center rounded border border-gray-300 bg-white p-0 text-gray-600 shadow-sm active:bg-gray-50"
                                            >
                                                <Calendar className="h-4 w-4" />
                                            </button>
                                        )}
                                        selectsRange
                                        startDate={parseLocalDateInput(sharedStartDate)}
                                        endDate={parseLocalDateInput(sharedEndDate)}
                                        onChange={handleSharedDateChange}
                                        isClearable={false}
                                        placeholderText=""
                                        onKeyDown={(e) => {
                                            e.preventDefault();
                                        }}
                                        popperPlacement="bottom-end"
                                        popperClassName="ledger-date-picker-popper"
                                    />
                                </div>
                                <button
                                    onClick={() => handleRefresh()}
                                    disabled={isFetching || syncAccountingQboMutation.isPending || isRefreshSettling}
                                    aria-label="Refresh"
                                    title="Refresh"
                                    className="relative flex h-10 w-10 items-center justify-center rounded border bg-indigo-500 text-sm text-white shadow-sm hover:bg-indigo-600 disabled:opacity-80"
                                >
                                    <RefreshCw size={16} className={syncAccountingQboMutation.isPending || isRefreshSettling ? 'animate-spin' : ''} />
                                </button>
                            </div>
                        </div>
                        <div className="flex w-full flex-wrap items-stretch justify-end gap-3 lg:w-auto lg:items-start">
                            <div className={`relative hidden w-full items-center gap-2 rounded border border-gray-300 bg-white px-4 py-2 shadow-sm sm:flex sm:w-64 ${selectedTransactionId ? 'z-10 pointer-events-none opacity-70' : 'z-30'}`}>
                                <Calendar className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                <DatePicker
                                    wrapperClassName="w-full"
                                    selectsRange
                                    startDate={parseLocalDateInput(sharedStartDate)}
                                    endDate={parseLocalDateInput(sharedEndDate)}
                                    onChange={handleSharedDateChange}
                                    isClearable
                                    placeholderText="Select date range"
                                    onKeyDown={(e) => {
                                        e.preventDefault();
                                    }}
                                    className="text-sm p-0 border-none focus:ring-0 focus:outline-none focus-visible:outline-none w-full caret-transparent cursor-pointer"
                                    open={selectedTransactionId ? false : undefined}
                                    popperPlacement="bottom-end"
                                    popperClassName="ledger-date-picker-popper"
                                />
                            </div>

                            {!isAdminEmbedded && businessOptions.length > 1 && (
                                <SearchableBusinessSelect
                                    options={businessOptions}
                                    value={selectedBusinessId}
                                    onChange={(id) => {
                                        setCashFilters((prev) => ({ ...prev, business_id: id, qbo_account_id: undefined, page: 1 }));
                                        setDatasetFilters((prev) => ({ ...prev, business_id: id, page: 1 }));
                                        setActiveBusinessId(id);
                                    }}
                                    placeholder="Select business"
                                    emptyLabel="No businesses found"
                                    widthClassName="w-full sm:w-80"
                                    showSubtitleWhenClosed={false}
                                />
                            )}

                            <div ref={viewModeMenuRef} className="relative hidden w-full sm:block sm:w-64">
                                <button
                                    type="button"
                                    data-tutorial="ledger-view-select"
                                    onClick={() => setIsViewModeMenuOpen((current) => !current)}
                                    className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white py-2.5 pl-3 pr-3 text-left text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                >
                                    <span>{LEDGER_VIEW_OPTIONS.find((option) => option.value === ledgerViewMode)?.label || 'Cash View'}</span>
                                    <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isViewModeMenuOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {isViewModeMenuOpen && (
                                    <div
                                        data-tutorial="ledger-view-menu"
                                        className="absolute right-0 top-[calc(100%+4px)] z-50 w-full overflow-hidden rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg"
                                    >
                                        {LEDGER_VIEW_OPTIONS.map((option) => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() => {
                                                    setLedgerViewMode(option.value);
                                                    setIsViewModeMenuOpen(false);
                                                }}
                                                className={`block w-full px-3 py-2 text-left hover:bg-gray-50 ${option.value === ledgerViewMode ? 'text-gray-900' : 'text-gray-700'}`}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={() => handleRefresh()}
                                disabled={isFetching || syncAccountingQboMutation.isPending || isRefreshSettling}
                                aria-label="Refresh"
                                title="Refresh"
                                className="relative hidden h-11 w-10 items-center justify-center rounded border bg-indigo-500 text-sm text-white shadow-sm hover:bg-indigo-600 disabled:opacity-80 sm:flex sm:w-auto sm:px-6"
                            >
                                <span>
                                    {syncAccountingQboMutation.isPending || isRefreshSettling ? 'Refreshing…' : 'Refresh'}
                                </span>
                            </button>
                        </div>
                    </div>

                    <div className="relative mb-3 sm:hidden">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={mobileSearchValue}
                            onChange={(event) => handleMobileSearchChange(event.target.value)}
                            placeholder={mobileSearchPlaceholder}
                            className="h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                    </div>

                    <div className="relative flex-1 min-h-0 flex flex-col">
                        <div data-tutorial="ledger-table" className="min-h-0 flex-1 flex flex-col">
                            {ledgerViewMode === 'cash' ? (
                                <CashView
                                    filterPanel={isMobile ? null : cashFilterPanel}
                                    agentAppliedFilters={agentAppliedFilters}
                                    embedded={isAdminEmbedded}
                                    table={(
                                        <LedgerTable
                                            transactions={cashData?.items || []}
                                            isLoading={isCashLoading}
                                            onRowClick={setSelectedTransactionId}
                                            total={cashData?.total || 0}
                                            page={cashFilters.page || 1}
                                            pageSize={cashFilters.page_size || 50}
                                            onPageChange={handleCashPageChange}
                                            hasActiveFilters={hasActiveCashFilters}
                                            sortBy={cashFilters.sort_by}
                                            sortOrder={cashFilters.sort_order}
                                            onSort={handleSort}
                                            selectedIds={selectedIds}
                                            onSelectionChange={setSelectedIds}
                                        />
                                    )}
                                    batchActionBar={ledgerViewMode === 'cash' && selectedIds.length > 0 ? (
                                        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-white shadow-lg border border-gray-200 rounded-lg px-6 py-3 flex items-center gap-6 z-10 animate-in fade-in slide-in-from-bottom-4 duration-200">
                                            <span className="text-sm font-medium text-gray-700">
                                                {selectedIds.length} transaction{selectedIds.length !== 1 ? 's' : ''} selected
                                            </span>

                                            <div className="h-6 w-px bg-gray-200"></div>

                                            <button
                                                onClick={handleBatchApprove}
                                                disabled={isBatchUpdating}
                                                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
                                            >
                                                {isBatchUpdating ? 'Processing...' : (
                                                    <>
                                                        <CheckCircle size={16} />
                                                        Approve Selected
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    ) : null}
                                />
                            ) : ledgerViewMode === 'inventory' ? (
                                <InventoryView
                                    filterPanel={isMobile ? null : accountingFilterPanel}
                                    embedded={isAdminEmbedded}
                                    table={datasetTable}
                                />
                            ) : ledgerViewMode === 'ar' && isApWorkspaceSource(arSource) ? (
                                <APView
                                    filterPanel={isMobile ? null : accountingFilterPanel}
                                    embedded={isAdminEmbedded}
                                    table={datasetTable}
                                />
                            ) : ledgerViewMode === 'ar' ? (
                                <ARView
                                    filterPanel={isMobile ? null : accountingFilterPanel}
                                    embedded={isAdminEmbedded}
                                    table={datasetTable}
                                />
                            ) : (
                                <AccountingView
                                    filterPanel={isMobile ? null : accountingFilterPanel}
                                    embedded={isAdminEmbedded}
                                    table={datasetTable}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <TransactionDrawer
                transactionId={selectedTransactionId}
                open={!!selectedTransactionId}
                onClose={() => {
                    setSelectedTransactionId(null);
                    setPendingNormalizationEditorRequest(null);
                }}
                businessId={cashFilters.business_id}
                onNormalizationEditorRequestConsumed={() => setPendingNormalizationEditorRequest(null)}
                normalizationEditorRequest={
                    pendingNormalizationEditorRequest && pendingNormalizationEditorRequest.transactionId === selectedTransactionId
                        ? {
                            token: pendingNormalizationEditorRequest.token,
                            matchType: pendingNormalizationEditorRequest.matchType,
                            matchValue: pendingNormalizationEditorRequest.matchValue,
                            normalizedDescription: pendingNormalizationEditorRequest.normalizedDescription,
                            vendorName: pendingNormalizationEditorRequest.vendorName,
                        }
                        : null
                }
            />

            <LedgerRecordDrawer
                open={!!selectedDatasetItem}
                source={selectedDatasetSource || (ledgerViewMode === 'inventory' ? 'inventory' : ledgerViewMode === 'ar' ? arSource : (accountingSource as AccountingLedgerSource))}
                record={selectedDatasetItem}
                businessId={datasetFilters.business_id}
                onClose={() => {
                    setSelectedDatasetItem(null);
                    setSelectedDatasetSource(null);
                    if (deepLinkQboBillId) {
                        const nextParams = new URLSearchParams(searchParams);
                        nextParams.delete('qbo_bill_id');
                        nextParams.delete('source');
                        nextParams.delete('view');
                        setSearchParams(nextParams, { replace: true });
                    }
                }}
                onReviewDocument={drawerIsUploadedSource ? (record) => {
                    setReviewDocumentRecord(record);
                    setReviewDocumentType(
                        selectedDatasetSource === 'uploaded_invoices'
                            ? 'invoice'
                            : selectedDatasetSource === 'uploaded_expenses'
                                ? 'expenses'
                                : 'bills'
                    );
                } : undefined}
                onOpenLinkedRecord={handleOpenLinkedRecord}
                onOpenDocumentFile={drawerIsUploadedSource ? handleOpenDocumentFile : undefined}
                onOpenArInvoice={handleOpenArInvoiceFromAr}
                onOpenArCustomer={handleOpenArCustomer}
                onOpenApBill={handleOpenApBillFromVendor}
                onOpenQboTransaction={handleOpenQboTransactionFromAr}
                onOpenCashTransaction={handleOpenCashTransactionFromAr}
                onOpenArPayment={handleOpenArPaymentFromCustomer}
            />

            <VendorDirectoryModal
                isOpen={vendorDirectorySource !== null}
                onClose={() => setVendorDirectorySource(null)}
                source={vendorDirectorySource || 'aliases'}
                businessId={selectedBusinessId || undefined}
            />

            <ArWeeklySummaryModal
                isOpen={isArWeeklySummaryOpen}
                onClose={() => setIsArWeeklySummaryOpen(false)}
                metric={isTutorialDemo ? ledgerTutorialArMetric : latestArWeeklyMetric || null}
                actions={isTutorialDemo ? ledgerTutorialArActions : arWeeklyActions}
                contributions={isTutorialDemo ? ledgerTutorialArContributions : arWeeklyRevenueContributions}
                onOpenArInvoice={handleOpenArInvoiceFromAr}
                onOpenArCustomer={handleOpenArCustomer}
            />

            <WeeklySummaryModal
                open={isUnifiedWeeklySummaryOpen}
                businessId={selectedBusinessId || undefined}
                onClose={() => setIsUnifiedWeeklySummaryOpen(false)}
                title={ledgerViewMode === 'ar' && isApWorkspaceSource(arSource) ? 'AP Weekly Summary' : 'Weekly Summary'}
            />

            <VendorManagementModal
                isOpen={isVendorManagementOpen}
                onClose={() => setIsVendorManagementOpen(false)}
                businessId={cashFilters.business_id}
            />

            <StandardAccountSelector
                isOpen={isGlCodeFinzSelectorOpen}
                onClose={() => setIsGlCodeFinzSelectorOpen(false)}
                currentAccountId={null}
                businessId={selectedBusinessId || undefined}
                onSelect={(account) => {
                    setGlCodeFinzCategory(account.fiscal_bucket || '');
                    setIsGlCodeFinzSelectorOpen(false);
                }}
            />

            {reviewDocumentRecord && reviewDocumentType && (
                <InvoiceConfirmationModal
                    documentType={reviewDocumentType}
                    documentId={reviewDocumentRecord._id}
                    preFetchedInvoice={reviewDocumentRecord as unknown as DocInvoice}
                    onClose={() => {
                        setReviewDocumentRecord(null);
                        setReviewDocumentType(null);
                    }}
                    onConfirm={() => {
                        setReviewDocumentRecord(null);
                        setReviewDocumentType(null);
                        setSelectedDatasetItem(null);
                        refetchDatasetData();
                    }}
                    businessId={datasetFilters.business_id}
                />
            )}

            {!isAdmin && showSetupGuidePanel && !isMobile && (
                <div
                    aria-hidden="true"
                    className="relative h-full w-[30%] max-w-[300px] min-w-[350px] shrink-0 border bg-white shadow-sm"
                />
            )}

            {isAdmin && !!selectedBusinessId && (
                <div className={showChat && !showSetupGuidePanel ? (isMobile ? 'fixed inset-0 z-50 bg-white w-full max-w-none h-[100dvh]' : `flex bg-white h-full ${extendChat ? 'w-[40%] max-w-[600px]' : 'w-[30%] max-w-[300px]'} chat-panel`) : 'hidden'}>
                    <div className="flex-1 overflow-auto flex flex-col">
                        <LedgerChatPanel
                            businessId={selectedBusinessId || undefined}
                            onClose={() => setShowChat(false)}
                            onOpenTransaction={setSelectedTransactionId}
                            onApplyResultSet={handleApplyAgentResultSet}
                            onRefreshLedger={refetchCashData}
                            onOpenNormalizationEditor={(payload: LedgerAgentOpenNormalizationEditorPayload) => {
                                const transactionId = String(payload.transaction_id || '').trim();
                                if (!transactionId) return;
                                setSelectedTransactionId(transactionId);
                                setPendingNormalizationEditorRequest({
                                    transactionId,
                                    token: Date.now(),
                                    matchType: payload.suggested_match_type,
                                    matchValue: payload.suggested_match_value,
                                    normalizedDescription: payload.suggested_normalized_description,
                                    vendorName: payload.suggested_vendor_name,
                                });
                            }}
                        />
                    </div>

                    {!isMobile && (
                        <div
                            onClick={() => setExtendChat(!extendChat)}
                            className="border hover:bg-gray-200 hover:cursor-pointer py-4 rounded absolute bg-gray-100 top-20 -left-2"
                        >
                            {extendChat ? <ChevronRight size={18} className="text-gray-500" /> : <ChevronLeft size={18} className="text-gray-500" />}
                        </div>
                    )}
                </div>
            )}

            {!showChat && !showSetupGuidePanel && !isMobile && isAdmin && !!selectedBusinessId && <ChatButton handleShowChat={handleShowChat} />}
        </div>
    );
};

export default LedgerScreen;
